import { useState } from 'react';
import useNotification from './useNotification';
import conversationService from '../services/conversationService';

/**
 * Custom hook for CRUD operations on development guidelines with category support
 * Handles H2 (##) category headers and H3 (###) section accordions
 */
const useDevelopmentGuidelinesCRUD = (
  conversation,
  sections,
  setSections,
  setExpandedSections,
  contentRef,
  updateCurrentVersion,
  currentVersion,
  isLocalUpdateRef,
  currentVersionIndex = 0
) => {
  const { showNotification } = useNotification();
  const [editingSection, setEditingSection] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, section: null });

  /**
   * Reconstruct markdown content with proper H2 categories and H3 sections
   */
  const reconstructMarkdown = (sectionsList) => {
    let content = '';
    let lastCategory = null;

    sectionsList.forEach((section, idx) => {
      // Add category header if this is the first section in a new category
      if (section.isFirstInCategory && section.category && section.category !== lastCategory) {
        if (idx > 0) content += '\n';
        content += `## ${section.category}\n\n`;
        lastCategory = section.category;
      }

      // Add section header (H3)
      content += `### ${section.title}\n\n`;

      // Add section content
      if (section.content) {
        content += `${section.content}\n\n`;
      }
    });

    return content.trim();
  };

  /**
   * Recalculate isFirstInCategory flags after modifications
   */
  const recalculateCategories = (sectionsList) => {
    const seenCategories = new Set();
    return sectionsList.map(section => {
      if (section.category && !seenCategories.has(section.category)) {
        seenCategories.add(section.category);
        return { ...section, isFirstInCategory: true };
      } else {
        return { ...section, isFirstInCategory: false };
      }
    });
  };

  /**
   * Save changes to backend and update version
   */
  const saveToBackend = async (updatedContent, updatedSections) => {
    try {
      // Validate conversation exists
      if (!conversation || !conversation._id || !conversation.messages) {
        throw new Error('Invalid conversation data');
      }

      const updatedMessages = [...conversation.messages];

      // Find the message ID for the current version
      const currentVersionMessageId = currentVersion?.id;
      let messageUpdated = false;

      // Try to find and update the specific message by ID
      if (currentVersionMessageId) {
        for (let i = 0; i < updatedMessages.length; i++) {
          if (updatedMessages[i]._id === currentVersionMessageId ||
              updatedMessages[i].id === currentVersionMessageId) {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent
            };
            messageUpdated = true;
            break;
          }
        }
      }

      // Fallback: update the last assistant message if we couldn't find by ID
      if (!messageUpdated) {
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent
            };
            messageUpdated = true;
            break;
          }
        }
      }

      // Verify a message was updated
      if (!messageUpdated) {
        throw new Error('No assistant message found to update');
      }

      // Save sections to ParsedResponse — backend syncs markdown to conversation message
      let messageTimestamp = currentVersion?.timestamp;
      // Fallback: find timestamp from last assistant message in conversation
      if (!messageTimestamp && conversation?.messages?.length) {
        for (let i = conversation.messages.length - 1; i >= 0; i--) {
          if (conversation.messages[i].role === 'assistant') {
            messageTimestamp = conversation.messages[i].createdAt;
            break;
          }
        }
      }
      if (!messageTimestamp || !conversation._id) {
        throw new Error('Missing timestamp or conversation ID for save');
      }

      const result = await conversationService.updateParsedSections(
        conversation._id,
        messageTimestamp,
        updatedSections
      );
      if (result?.data?.markdown) {
        contentRef.current = result.data.markdown;
      }

      // Update the current version
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent,
          sections: updatedSections
        });
      }
    } catch (error) {
      console.error('Error in saveToBackend:', error);
      throw error; // Re-throw to be caught by calling function
    }
  };

  /**
   * Start editing a section
   */
  const handleStartEdit = (sectionId, content, title = '') => {
    setEditingSection(sectionId);
    setEditedContent(content);
    setEditedTitle(title);
  };

  /**
   * Cancel editing
   */
  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditedContent('');
    setEditedTitle('');
  };

  /**
   * Save edited section with category support
   */
  const handleSaveEdit = async (sectionId) => {
    if (!editedContent.trim()) {
      showNotification({
        message: "Content cannot be empty",
        type: "error"
      });
      return;
    }

    setIsSaving(true);

    try {
      // Validate sections exists
      if (!sections || sections.length === 0) {
        throw new Error('No sections available to update');
      }

      // Find and update the section (content and title)
      const updatedSections = sections.map(s => {
        if (s.id === sectionId) {
          return {
            ...s,
            content: editedContent,
            title: editedTitle || s.title // Use editedTitle if provided, otherwise keep original
          };
        }
        return s;
      });

      // Verify section was found
      const sectionFound = updatedSections.some(s => s.id === sectionId);
      if (!sectionFound) {
        throw new Error(`Section with id ${sectionId} not found`);
      }

      // Set the flag to prevent reset from currentVersion
      if (isLocalUpdateRef && isLocalUpdateRef.current !== undefined) {
        isLocalUpdateRef.current = true;
      }

      setSections(updatedSections);

      // Reconstruct the full content with proper section format and categories
      const updatedContent = reconstructMarkdown(updatedSections);

      // Update content ref
      if (contentRef && contentRef.current !== undefined) {
        contentRef.current = updatedContent;
      }

      // Save to backend
      await saveToBackend(updatedContent, updatedSections);

      // Exit edit mode
      setEditingSection(null);
      setEditedContent('');
      setEditedTitle('');

      showNotification({
        message: `Section updated successfully in version ${currentVersionIndex + 1}`,
        type: "success"
      });
    } catch (error) {
      console.error("Error saving section:", error);
      showNotification({
        message: `Failed to save changes in version ${currentVersionIndex + 1}: ${error.message}`,
        type: "error"
      });
    } finally {
      // Ensure isSaving is always reset
      setIsSaving(false);
    }
  };

  /**
   * Open delete confirmation dialog
   */
  const handleDeleteSection = (section) => {
    setDeleteConfirmation({ open: true, section });
  };

  /**
   * Delete section with category support
   */
  const handleDeleteSectionConfirmed = async () => {
    const section = deleteConfirmation.section;
    setDeleteConfirmation({ open: false, section: null });

    if (!section) return;

    try {
      // Remove the section from the sections array
      let updatedSections = sections.filter(s => s.id !== section.id);

      // Recalculate isFirstInCategory for remaining sections
      updatedSections = recalculateCategories(updatedSections);

      // Set the flag to prevent reset from currentVersion
      if (isLocalUpdateRef) {
        isLocalUpdateRef.current = true;
      }

      setSections(updatedSections);

      // Remove expanded state for this section
      setExpandedSections(prev => {
        const updated = { ...prev };
        delete updated[section.id];
        return updated;
      });

      // Reconstruct the full content with proper section format and categories
      const updatedContent = reconstructMarkdown(updatedSections);

      // Update content ref
      contentRef.current = updatedContent;

      // Save to backend
      await saveToBackend(updatedContent, updatedSections);

      showNotification({
        message: `"${section.title}" deleted successfully in version ${currentVersionIndex + 1}`,
        type: 'success'
      });

    } catch (error) {
      console.error('Error deleting section:', error);
      showNotification({
        message: `Failed to delete "${section.title}" in version ${currentVersionIndex + 1}`,
        type: 'error'
      });
    }
  };

  /**
   * Copy section to clipboard
   */
  const handleCopySection = (section) => {
    try {
      let sectionText = `${section.title}\n`;
      sectionText += `${'-'.repeat(section.title.length)}\n\n`;
      sectionText += section.content || '';

      navigator.clipboard.writeText(sectionText);
      showNotification({
        message: `"${section.title}" copied to clipboard`,
        type: "success"
      });
    } catch (error) {
      showNotification({
        message: `Failed to copy "${section.title}" to clipboard`,
        type: "error"
      });
    }
  };

  /**
   * Add new section with category support
   */
  const handleAddSection = async (newSectionData) => {
    try {
      // Generate section format for display
      const newSection = {
        id: newSectionData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        title: newSectionData.title,
        content: newSectionData.content,
        rawContent: newSectionData.content,
        category: newSectionData.category || sections[sections.length - 1]?.category,
        isFirstInCategory: false
      };

      // Add to sections array
      let updatedSections = [...sections, newSection];

      // Recalculate categories
      updatedSections = recalculateCategories(updatedSections);

      // Set the flag to prevent reset from currentVersion
      if (isLocalUpdateRef) {
        isLocalUpdateRef.current = true;
      }

      setSections(updatedSections);

      // Reconstruct full markdown with new section
      const updatedContent = reconstructMarkdown(updatedSections);

      // Update content ref
      contentRef.current = updatedContent;

      // Save to backend
      await saveToBackend(updatedContent, updatedSections);

      showNotification({
        message: `Section added successfully in version ${currentVersionIndex + 1}`,
        type: 'success'
      });

      return true;
    } catch (error) {
      console.error('Error adding section:', error);
      showNotification({
        message: `Failed to add new section in version ${currentVersionIndex + 1}`,
        type: 'error'
      });
      return false;
    }
  };

  return {
    // State
    editingSection,
    editedContent,
    editedTitle,
    isSaving,
    deleteConfirmation,

    // Setters
    setEditedContent,
    setEditedTitle,
    setDeleteConfirmation,
    setEditingSection,
    setIsSaving,

    // Handlers
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteSection,
    handleDeleteSectionConfirmed,
    handleCopySection,
    handleAddSection,

    // Utilities
    reconstructMarkdown,
    recalculateCategories
  };
};

export default useDevelopmentGuidelinesCRUD;
